import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLandingData } from "../../../hooks/useLandingData";

function PhoneVisual({ active }: { active: boolean }) {
  const { t } = useTranslation();
  const [ring, setRing] = useState(false);
  useEffect(() => {
    if (!active) { setRing(false); return; }
    const timer = setTimeout(() => setRing(true), 300);
    return () => clearTimeout(timer);
  }, [active]);

  return (
    <div className="flex flex-col items-center justify-center h-full gap-4">
      <div className={`relative w-20 h-20 flex items-center justify-center rounded-full bg-emerald-500/20 border-2 border-emerald-400/40 transition-all duration-500 ${ring ? "scale-110" : "scale-100"}`}>
        {ring && <span className="absolute inset-0 rounded-full border-2 border-emerald-400/30 animate-ping" />}
        {ring && <span className="absolute inset-[-12px] rounded-full border border-emerald-400/15 animate-ping" style={{ animationDelay: "0.3s" }} />}
        <i className={`ri-phone-incoming-line text-4xl text-emerald-400 transition-all duration-300 ${ring ? "rotate-12" : ""}`} />
      </div>
      {ring && (
        <div className="flex flex-col items-center gap-1">
          <p className="text-white text-sm font-semibold">+34 612 345 678</p>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-emerald-400 text-xs font-medium">{t("how_incoming_call")}</span>
          </div>
          <div className="mt-2 flex gap-2">
            <div className="px-3 py-1.5 bg-emerald-500 rounded-xl text-xs text-white font-medium flex items-center gap-1">
              <i className="ri-phone-line" /> {t("how_step_label")} 1
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AIVisual({ active }: { active: boolean }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const labels = [
    t("ai_detecting"),
    t("ai_querying"),
    t("ai_analyzing"),
    t("ai_generating"),
  ];

  useEffect(() => {
    if (!active) { setStep(0); return; }
    const timer = setInterval(() => setStep((p) => (p + 1) % 4), 900);
    return () => clearInterval(timer);
  }, [active]);

  const bars = [55, 80, 40, 95, 65, 70, 50, 88, 45, 75, 60, 82];

  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 px-4">
      <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-400/20 border-2 border-amber-400/40 relative">
        {active && <span className="absolute inset-0 rounded-full border-2 border-amber-400/20 animate-ping" />}
        <i className="ri-robot-2-line text-3xl text-amber-400" />
      </div>
      <div className="w-full flex items-end justify-center gap-1 h-10">
        {bars.map((h, i) => (
          <div
            key={i}
            className="rounded-full transition-all duration-150"
            style={{
              width: "5px",
              height: active ? `${h * 0.38 + 4}px` : "4px",
              backgroundColor: i % 3 === 0 ? "#F59E0B" : "rgba(245,158,11,0.3)",
              transitionDelay: `${i * 50}ms`,
            }}
          />
        ))}
      </div>
      {active && (
        <div className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-center">
          <p className="text-amber-300 text-xs font-medium animate-pulse">{labels[step]}</p>
        </div>
      )}
    </div>
  );
}

function CalendarVisual({ active }: { active: boolean }) {
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    if (!active) { setConfirmed(false); return; }
    const timer = setTimeout(() => setConfirmed(true), 800);
    return () => clearTimeout(timer);
  }, [active]);

  const slots = ["09:00", "09:30", "10:00", "10:30", "11:00"];

  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 px-2">
      {!confirmed ? (
        <>
          <p className="text-white/60 text-xs text-center">{t("how_check_availability")}</p>
          <div className="w-full space-y-1.5">
            {slots.map((s, i) => (
              <div
                key={s}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg"
                style={{ opacity: active ? 1 : 0, transition: `opacity 0.3s ease ${i * 0.15}s` }}
              >
                <i className="ri-time-line text-stone-500 text-xs" />
                <span className="text-stone-400 text-xs">{s}</span>
                {i === 1 && active && (
                  <span className="ml-auto text-xs text-emerald-400 font-medium flex items-center gap-1">
                    <i className="ri-check-line" /> Free
                  </span>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="w-full flex flex-col items-center gap-3">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-500/20 border-2 border-emerald-400/50">
            <i className="ri-calendar-check-2-line text-2xl text-emerald-400" />
          </div>
          <div className="w-full bg-emerald-500/10 border border-emerald-400/30 rounded-xl p-3 text-center">
            <p className="text-emerald-300 text-xs font-bold mb-0.5">{t("how_appointment_confirmed")}</p>
            <p className="text-white text-xs">Tue 24 · 09:30h</p>
            <p className="text-stone-400 text-xs mt-1">Dr. Martínez · 30 min</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-stone-400">
            <i className="ri-mail-send-line text-amber-400" /> {t("how_email_sent")}
            <i className="ri-smartphone-line text-amber-400 ml-1" /> {t("how_sms_sent")}
          </div>
        </div>
      )}
    </div>
  );
}

export default function HowItWorksSection() {
  const { t } = useTranslation();
  const { howItWorksSteps } = useLandingData();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || paused) return;
    const timer = setInterval(() => setActiveStep((p) => (p + 1) % howItWorksSteps.length), 3200);
    return () => clearInterval(timer);
  }, [visible, paused, howItWorksSteps.length]);

  const handleStepClick = useCallback((i: number) => {
    setActiveStep(i);
    setPaused(true);
    const timer = setTimeout(() => setPaused(false), 6000);
    return () => clearTimeout(timer);
  }, []);

  const connectorPct = (activeStep / (howItWorksSteps.length - 1)) * 100;

  return (
    <section
      ref={ref}
      className="py-28"
      style={{ background: "linear-gradient(180deg, #FAFAF8 0%, #F0EBE3 100%)" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">
            {t("how_label")}
          </span>
          <h2
            className="text-4xl lg:text-5xl font-bold text-stone-900 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("how_title_1")}
            <br />
            <span className="text-amber-500">{t("how_title_2")}</span>
          </h2>
          <p className="mt-4 text-stone-500 text-lg max-w-xl mx-auto">
            {t("how_subtitle")}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 items-start">
          <div className="lg:col-span-3 space-y-4">
            <div className="relative">
              <div className="absolute left-[26px] top-8 bottom-8 w-px bg-stone-200 overflow-hidden">
                <div
                  className="absolute top-0 left-0 right-0 bg-amber-400 transition-all duration-700 ease-in-out"
                  style={{ height: `${connectorPct}%` }}
                />
              </div>

              {howItWorksSteps.map((step, i) => {
                const isActive = activeStep === i;
                const isPast = i < activeStep;
                return (
                  <button
                    key={step.number}
                    onClick={() => handleStepClick(i)}
                    className={`relative w-full text-left flex gap-5 p-5 rounded-2xl border transition-all duration-400 cursor-pointer ${
                      isActive
                        ? "border-amber-200 bg-amber-50/80"
                        : "border-transparent bg-transparent hover:border-stone-200 hover:bg-white"
                    } ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"}`}
                    style={{ transitionDelay: `${i * 100}ms` }}
                  >
                    <div
                      className={`relative z-10 w-[52px] h-[52px] flex-shrink-0 flex items-center justify-center rounded-2xl border-2 transition-all duration-300 ${
                        isActive
                          ? "bg-amber-500 border-amber-500 text-white scale-105"
                          : isPast
                          ? "bg-emerald-500 border-emerald-500 text-white"
                          : "bg-white border-stone-200 text-stone-400"
                      }`}
                    >
                      {isPast && !isActive ? (
                        <i className="ri-check-line text-xl" />
                      ) : (
                        <i className={`${step.icon} text-xl`} />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold tracking-widest ${isActive ? "text-amber-600" : "text-stone-400"}`}>
                          {t("how_step_label")} {step.number}
                        </span>
                        {isActive && (
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                        )}
                      </div>
                      <h3 className={`font-bold text-base mb-1 transition-colors duration-300 ${isActive ? "text-stone-900" : "text-stone-600"}`}>
                        {step.title}
                      </h3>
                      <p
                        className={`text-sm leading-relaxed transition-all duration-300 ${
                          isActive ? "text-stone-500 max-h-40 opacity-100" : "text-stone-400 max-h-10 opacity-80 overflow-hidden"
                        }`}
                      >
                        {step.description}
                      </p>

                      {isActive && (
                        <ul className="mt-3 grid grid-cols-2 gap-1.5">
                          {step.details.map((d) => (
                            <li key={d} className="flex items-start gap-1.5">
                              <i className="ri-check-line text-amber-500 text-xs mt-0.5 flex-shrink-0" />
                              <span className="text-stone-500 text-xs leading-snug">{d}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-3 px-4 py-3 bg-stone-900 rounded-2xl">
              <i className="ri-timer-flash-line text-amber-400 text-xl" />
              <p className="text-white text-sm">
                {t("how_total_time")}{" "}
                <strong className="text-amber-400">{t("how_total_time_value")}</strong>
              </p>
            </div>
          </div>

          <div
            className={`lg:col-span-2 transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}
          >
            <div
              className="rounded-3xl overflow-hidden border border-stone-800 sticky top-24"
              style={{ background: "#111113" }}
            >
              <div className="px-5 py-3.5 border-b border-white/8 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img
                    src="https://static.readdy.ai/image/464d6b2d88c2c0b329c43db37f7a6b21/5e3d82f418168fbdbc49efd95d2ee821.png"
                    alt="VoiceAI"
                    className="h-5 w-auto brightness-0 invert opacity-80"
                  />
                  <span className="text-stone-500 text-xs">{t("how_live_simulation")}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-400" />
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                </div>
              </div>

              <div className="p-6">
                <div className="flex gap-2 mb-6">
                  {howItWorksSteps.map((s, i) => (
                    <button
                      key={s.number}
                      onClick={() => handleStepClick(i)}
                      className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        activeStep === i
                          ? "bg-amber-500 text-white"
                          : activeStep > i
                          ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                          : "bg-white/5 text-stone-600 border border-white/8"
                      }`}
                    >
                      {s.number}
                    </button>
                  ))}
                </div>

                <div className="h-56 flex items-center justify-center">
                  {activeStep === 0 && <PhoneVisual active={visible} />}
                  {activeStep === 1 && <AIVisual active={visible} />}
                  {activeStep === 2 && <CalendarVisual active={visible} />}
                </div>

                <div className="mt-4 pt-4 border-t border-white/8">
                  <p className="text-stone-400 text-xs text-center leading-relaxed">
                    <span className="text-amber-400 font-semibold">{howItWorksSteps[activeStep].subtitle}</span>
                    {" · "}{howItWorksSteps[activeStep].title}
                  </p>
                </div>
              </div>

              <div className="px-5 pb-4 flex items-center justify-center gap-2">
                {howItWorksSteps.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => handleStepClick(i)}
                    className={`rounded-full transition-all duration-300 cursor-pointer ${
                      activeStep === i ? "w-6 h-2 bg-amber-400" : "w-2 h-2 bg-white/15 hover:bg-white/30"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
